// The credential change history (caiet UC21-10, §7.10 REUSE-05): the wireframe's
// "Istoricul modificărilor" card on AdminCredentiale, and the "Istoric" row
// action of CredentialeAdmin, over the same three columns as every other history
// surface in the system.
// per docs/shape-uc-31-credential-history.md §Approach
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudModal, MudInfoBox, MudTable } from '@egov-moldova/mud-react';
import { EmptyRegisterMessage, formatDateTime, onModalDismiss } from '@simsm/ui-shared';
import { apiClient } from '../../app/api-client';
import { HistoryActor } from '../../features/history/CatalogHistoryLog';

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
}

export interface CredentialHistoryEntry {
  id: string;
  changeType: string;
  actorId: string;
  actorRole: string;
  recordedAtIso: string;
  addedAddresses: string[];
  removedAddresses: string[];
}

// The two acts whose sentence already names the address (UC21-08), so the detail
// lines below would only repeat it.
const ADDRESS_IN_HEADLINE = new Set(['IP_SUSPENDED', 'IP_RESUMED']);

export async function fetchCredentialHistory(scope: {
  clientId: string;
  registrarId: string;
}): Promise<CredentialHistoryEntry[]> {
  const { data } = await apiClient.get<{ entries: CredentialHistoryEntry[] }>(
    `/admin/credentials/${encodeURIComponent(scope.clientId)}/history`,
    scope.registrarId ? { params: { registrarId: scope.registrarId } } : {},
  );
  return data.entries ?? [];
}

function useCredentialHistory(scope: { clientId: string; registrarId: string }) {
  return useQuery({
    queryKey: ['admin-credential-history', scope.clientId],
    queryFn: () => fetchCredentialHistory(scope),
  });
}

function headline(
  entry: Pick<CredentialHistoryEntry, 'changeType' | 'addedAddresses' | 'removedAddresses'>,
  t: ReturnType<typeof useTranslate>['t'],
): string {
  const addresses = [...entry.addedAddresses, ...entry.removedAddresses].join(', ');
  return t(`credentials.history.change.${entry.changeType}`, { addresses });
}

// What one credential event did, in words: the headline, and the addresses it
// added or removed when the headline does not already name them. Exported so the
// Registrar file's "Istoric" (spec 052) tells a credential event the way this
// card does, rather than growing a second wording for the same row.
export function CredentialChange({
  entry,
}: {
  entry: Pick<CredentialHistoryEntry, 'changeType' | 'addedAddresses' | 'removedAddresses'>;
}): JSX.Element {
  const { t } = useTranslate();
  const showDetails = !ADDRESS_IN_HEADLINE.has(entry.changeType);
  return (
    <>
      <span>{headline(entry, t)}</span>
      {showDetails && entry.addedAddresses.length > 0 ? (
        <span className="simsm-history__meta">
          {t('credentials.history.detail.added', {
            addresses: entry.addedAddresses.join(', '),
          })}
        </span>
      ) : null}
      {showDetails && entry.removedAddresses.length > 0 ? (
        <span className="simsm-history__meta">
          {t('credentials.history.detail.removed', {
            addresses: entry.removedAddresses.join(', '),
          })}
        </span>
      ) : null}
    </>
  );
}

// The two slotted cells of one row; the `when` cell is the plain value mud-table
// prints itself. A fragment, so the spans stay direct children of the table,
// which is where its named slots are looked up.
function CredentialHistoryCells({
  entry,
  index,
}: {
  entry: CredentialHistoryEntry;
  index: number;
}): JSX.Element {
  return (
    <>
      <span slot={`cell-change-${index}`} className="simsm-syscred__cell--stack">
        <CredentialChange entry={entry} />
      </span>
      <span slot={`cell-actor-${index}`} className="simsm-syscred__cell--stack">
        {/* The same renderer as the three catalog history surfaces, so this
            screen cannot drift back to two states: it used to print "Sistem"
            for a row with no actor, which data-model §1 forbids because it
            asserts a machine did work nobody recorded an actor for.

            The trail keeps the masked IDNP and the role and never a name
            (CF-63, SEC-14, BUG-1-009): the cell reads the id, else the role
            (a role with no id is still a person). This trail has no `source`
            column (an act on a credential is always somebody's), so no source
            is passed and the row reads either the actor or "not recorded".
            `HistoryActor` reads the id, the role and the source and nothing
            else, so a response that still carried a name cannot show it. */}
        <HistoryActor entry={entry} />
      </span>
    </>
  );
}

function CredentialHistoryTable({ entries }: { entries: CredentialHistoryEntry[] }): JSX.Element {
  const { t } = useTranslate();
  const columns: TableColumn[] = [
    { key: 'when', label: t('history.column.when') },
    { key: 'change', label: t('history.column.change') },
    { key: 'actor', label: t('history.column.actor') },
  ];

  return (
    <div className="simsm-syscred__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={entries.map((entry) => ({
          id: entry.id,
          when: formatDateTime(entry.recordedAtIso),
          change: headline(entry, t),
          actor: '',
        }))}
        rowStyle="divided"
        disableSort={true}
        aria-label={t('credentials.history.table.aria_label')}
      >
        <EmptyRegisterMessage>{t('credentials.history.empty')}</EmptyRegisterMessage>
        {entries.map((entry, index) => (
          <CredentialHistoryCells key={entry.id} entry={entry} index={index} />
        ))}
      </MudTable>
    </div>
  );
}

function CredentialHistoryBody({
  entries,
  isLoading,
  isError,
}: {
  entries: CredentialHistoryEntry[];
  isLoading: boolean;
  isError: boolean;
}): JSX.Element {
  const { t } = useTranslate();

  if (isError) {
    return (
      <MudInfoBox emphasis="strong" role="alert" variant="error">
        {t('common.load_error')}
      </MudInfoBox>
    );
  }
  if (isLoading) {
    return (
      <p role="status" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }
  // No entry is not an absent log: the table keeps its head and says so in one
  // cell spanning it (the wireframe's shape, IstoricLocalitati.tsx:54).
  return <CredentialHistoryTable entries={entries} />;
}

// The wireframe's third card on the Administrator Registratorului's screen.
export function CredentialHistoryCard({
  clientId,
  registrarId,
}: {
  clientId: string;
  registrarId: string;
}): JSX.Element {
  const { t } = useTranslate();
  const history = useCredentialHistory({ clientId, registrarId });

  return (
    <section className="simsm-syscred__card">
      <div>
        <h2 className="simsm-syscred__card-title">{t('credentials.history.title')}</h2>
        <p className="simsm-syscred__card-lede">{t('credentials.history.lede')}</p>
      </div>
      <CredentialHistoryBody
        entries={history.data ?? []}
        isLoading={history.isLoading}
        isError={history.isError}
      />
      <p className="simsm-history__modal-note">{t('credentials.history.note')}</p>
    </section>
  );
}

// The register's "Istoric" row action (caiet UC21-08/10).
export function CredentialHistoryModal({
  onClose,
  registrarName,
  clientId,
  registrarId,
}: {
  onClose: () => void;
  registrarName: string;
  clientId: string;
  registrarId: string;
}): JSX.Element {
  const { t } = useTranslate();
  const history = useCredentialHistory({ clientId, registrarId });

  return (
    <MudModal
      closeLabel={t('common.close')}
      open
      size="lg"
      titleText={t('credentials.history.modal.title', { name: registrarName })}
      // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
      // components emit `mudClose` bubbling+composed, so a descendant closing inside this
      // modal reaches this handler too; the guard answers only a real dismissal.
      onMudClose={onModalDismiss(onClose)}
    >
      <CredentialHistoryBody
        entries={history.data ?? []}
        isLoading={history.isLoading}
        isError={history.isError}
      />
      <p className="simsm-history__modal-note">{t('credentials.history.note')}</p>
      <div slot="actions" className="simsm-syscred__modal-actions">
        <MudButton shape="circular" variant="neutral" size="md" onClick={onClose}>
          {t('credentials.common.close')}
        </MudButton>
      </div>
    </MudModal>
  );
}
